/* eslint-disable no-console */

/**
 * Auth session helpers (frontend).
 *
 * Access token and user profile live in memory only (OWASP A03/A07).
 * Refresh token must be an HttpOnly cookie set by the backend — never in JS.
 *
 * Role helpers (hasRole, isAdmin, …) are UX-only. Real authorization is enforced
 * by the API (403). Do not treat client-side roles as access control (A01).
 */

// --- Legacy cookie helpers (non-auth use / migration cleanup only) ---

export const getCookie = name => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value || ''}${expires}; path=/; SameSite=Strict${secure}`;
};

export const eraseCookie = name => {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict`;
};

const LEGACY_AUTH_COOKIES = ['token', 'refreshToken', 'user'];

export const clearLegacyAuthCookies = () => {
  LEGACY_AUTH_COOKIES.forEach(eraseCookie);
};

// Clear any pre-migration auth cookies once on load
clearLegacyAuthCookies();

// --- In-memory session store ---

let accessToken = null;
let currentUser = null;
const listeners = new Set();

const notifyAuthListeners = () => {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (err) {
      console.error('Auth listener error:', err);
    }
  });
};

/** Subscribe to auth state changes. Returns unsubscribe. */
export const subscribeAuth = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getAccessToken = () => accessToken;

/** @deprecated Prefer getAccessToken */
export const getAuthToken = () => accessToken;

export const setAccessToken = token => {
  accessToken = token || null;
  notifyAuthListeners();
};

export const clearAccessToken = () => {
  accessToken = null;
  notifyAuthListeners();
};

export const getCurrentUser = () => currentUser;

export const setCurrentUser = user => {
  currentUser = user || null;
  notifyAuthListeners();
};

export const clearCurrentUser = () => {
  currentUser = null;
  notifyAuthListeners();
};

export const clearAuthState = () => {
  accessToken = null;
  currentUser = null;
  clearLegacyAuthCookies();
  notifyAuthListeners();
};

export const isAuthenticated = () => !!accessToken;

/**
 * @deprecated Refresh must not be readable from JS. Kept as no-op stub
 * so accidental callers do not read document.cookie.
 */
export const getRefreshToken = () => null;

/**
 * @deprecated Use setAccessToken. Refresh belongs in HttpOnly cookie (backend).
 */
export const setTokens = (token, _refreshToken) => {
  setAccessToken(token);
};

const ROLE_ID_MAP = {
  1: 'ADM',
  2: 'COR',
  3: 'MON',
  4: 'PRO',
  5: 'MBR',
};

/** UX-only — not authorization. Backend enforces access. */
export const hasRole = roleCode => {
  const user = getCurrentUser();
  if (!user) return false;

  const normalize = val =>
    val ? val.toString().toUpperCase().replace('ROLE_', '') : '';
  const target = normalize(roleCode);

  if (user.cd_role && normalize(user.cd_role) === target) return true;
  if (user.role) {
    if (
      typeof user.role === 'object' &&
      user.role.cd_role &&
      normalize(user.role.cd_role) === target
    )
      return true;
    if (typeof user.role === 'string' && normalize(user.role) === target)
      return true;
  }
  if (user.id_role) {
    const mappedCode = ROLE_ID_MAP[user.id_role];
    if (mappedCode && normalize(mappedCode) === target) return true;
  }

  return false;
};

/** UX-only */
export const isAdmin = () => hasRole('ADM');
/** UX-only */
export const isMonitor = () => hasRole('MON');
/** UX-only */
export const isCoordinator = () => hasRole('COR');
/** UX-only */
export const isProfessor = () => hasRole('PRO');

export const getUserRoleId = () => {
  const user = getCurrentUser();
  if (!user) return null;
  return user.id_role || null;
};

/** UX-only role code for navigation / menus */
export const getUserRoleCode = () => {
  const user = getCurrentUser();
  if (!user) return null;
  if (user.cd_role) return user.cd_role.toUpperCase();
  if (user.id_role)
    return (ROLE_ID_MAP[user.id_role] || null)?.toUpperCase() || null;
  return null;
};

/** Optional server logout hook registered by api.js to avoid circular imports */
let serverLogoutFn = null;

export const registerServerLogout = fn => {
  serverLogoutFn = fn;
};

/**
 * Clears local session, best-effort server logout, redirects to /login.
 */
export const logout = async ({ redirect = true } = {}) => {
  try {
    if (typeof serverLogoutFn === 'function') {
      await serverLogoutFn();
    }
  } catch (err) {
    console.warn('Server logout failed (continuing local clear):', err);
  }
  clearAuthState();
  if (redirect) {
    window.location.href = '/login';
  }
};
